"use client";

import { uploadArmyForgeImageAction } from "@/actions/uploadImage";
import { ImageFolder } from "@/data/types";
import { getCdnUrl } from "@/services";
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon
} from "@mui/icons-material";
import { Box, Button, CircularProgress, Typography, alpha, styled } from "@mui/material";
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1
});

const DropZone = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isDragOver" && prop !== "hasImage"
})<{ isDragOver?: boolean; hasImage?: boolean }>(({ theme, isDragOver, hasImage }) => ({
  border: `2px dashed ${isDragOver ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  textAlign: "center",
  transition: "all 0.2s ease",
  backgroundColor: isDragOver
    ? alpha(theme.palette.primary.main, 0.05)
    : hasImage
      ? alpha(theme.palette.success.main, 0.05)
      : "transparent",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
    borderColor: theme.palette.primary.main
  }
}));

const PreviewImage = styled("img")({
  maxWidth: "100%",
  maxHeight: 200,
  borderRadius: 8,
  objectFit: "contain"
});

type Props = {
  value?: string;
  onUpload?: (url: string) => void;
  onRemove?: () => void;
  assetType?: ImageFolder;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  accept?: string;
  maxSizeInBytes?: number;
  armyBookId?: string;
  forceShowRemove?: boolean;
};

interface ImageUploadRef {
  clearImage: () => void;
}

const ImageUpload = forwardRef<ImageUploadRef, Props>(
  (
    {
      value,
      onUpload,
      onRemove,
      assetType = "cover",
      label = "Upload Image",
      helperText,
      disabled = false,
      accept = "image/*",
      maxSizeInBytes = 4 * 1024 * 1024, // 4MB
      armyBookId = "",
      forceShowRemove
    },
    ref
  ) => {
    const [isUploading, setIsUploading] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      clearImage: () => {
        setError(null);
        onRemove?.();
      }
    }));

    const validateFile = (file: File): string | null => {
      if (!file.type.startsWith("image/")) {
        return "Please select a valid image file";
      }

      if (file.size > maxSizeInBytes) {
        const maxSizeMB = Math.round(maxSizeInBytes / (1024 * 1024));
        return `File size must be less than ${maxSizeMB}MB`;
      }

      return null;
    };

    const uploadFile = async (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      setIsUploading(true);
      setError(null);

      try {
        const formData = new FormData();
        formData.append("image", file);

        const result = await uploadArmyForgeImageAction(formData, assetType, armyBookId);

        if (result.success && result.path) {
          onUpload?.(result.path);
        } else {
          setError(result.error || "Upload failed");
        }
      } catch (error) {
        console.error("Upload error:", error);
        setError(error instanceof Error ? error.message : "Upload failed");
      } finally {
        setIsUploading(false);
      }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        uploadFile(file);
      }
      // Reset input value to allow selecting the same file again
      event.target.value = "";
    };

    const handleDrop = useCallback(
      (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(false);

        if (disabled || isUploading) return;

        const files = Array.from(event.dataTransfer.files);
        const imageFile = files.find((file) => file.type.startsWith("image/"));
        console.log("handleDrop", { files, imageFile });
        if (imageFile) {
          uploadFile(imageFile);
        } else {
          setError("Please drop a valid image file");
        }
      },
      [disabled, isUploading, uploadFile]
    );

    const handleDragOver = (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (!disabled && !isUploading) {
        setIsDragOver(true);
      }
    };

    const handleDragLeave = (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      // Only set to false if we're actually leaving the component
      // Check if relatedTarget is outside the current element
      if (event.currentTarget.contains(event.relatedTarget as Node)) {
        return;
      }
      setIsDragOver(false);
    };

    const handleClick = () => {
      if (!disabled && !isUploading && fileInputRef.current) {
        fileInputRef.current.click();
      }
    };

    const handleRemove = (event: React.MouseEvent) => {
      event.stopPropagation();
      setError(null);
      onRemove?.();
    };

    const removeButton = (
      <Button
        size="small"
        color="error"
        startIcon={<DeleteIcon />}
        onClick={handleRemove}
        disabled={disabled}
      >
        Remove
      </Button>
    );

    return (
      <Box>
        <DropZone
          isDragOver={isDragOver}
          hasImage={!!value}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleClick}
          sx={{
            opacity: disabled ? 0.6 : 1,
            cursor: disabled ? "not-allowed" : "pointer"
          }}
        >
          <VisuallyHiddenInput
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileSelect}
            disabled={disabled || isUploading}
          />

          {isUploading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2
              }}
            >
              <CircularProgress size={40} />
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary"
                }}
              >
                Uploading...
              </Typography>
            </Box>
          ) : value ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2
              }}
            >
              <PreviewImage src={getCdnUrl(value)} alt="Uploaded image" />
              <Box
                sx={{
                  display: "flex",
                  gap: 1
                }}
              >
                <Button size="small" startIcon={<CloudUploadIcon />} disabled={disabled}>
                  Replace Image
                </Button>
                {removeButton}
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2
              }}
            >
              <ImageIcon sx={{ fontSize: 48, color: "text.secondary" }} />
              <Typography
                variant="h6"
                sx={{
                  color: "text.primary"
                }}
              >
                {label}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary"
                }}
              >
                Drag and drop an image here, or click to select
              </Typography>
              <Button
                component="span"
                variant="contained"
                startIcon={<CloudUploadIcon />}
                disabled={disabled}
              >
                Choose File
              </Button>
              {forceShowRemove && onRemove && removeButton}
            </Box>
          )}
        </DropZone>
        {helperText && !error && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              mt: 1,
              display: "block"
            }}
          >
            {helperText}
          </Typography>
        )}
        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
            {error}
          </Typography>
        )}
      </Box>
    );
  }
);

export default ImageUpload;
